import React, { useState, useRef, useMemo, useContext, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import List from '~/containers/List';
import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputControl from '~/components/Input/InputControl';
import InputDate from '~/components/Input/InputDate';
import InputSelect from '~/components/Input/InputSelect';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import Request from '~/providers/Request';
import { URL } from '~/config/uri';
import maskCpf from '~/utils/maskCpf';
import WhatsappInvitationContext from './context';
import useStyles from './styles';

const PATH = '/admin/whatsapp_campaign';
const ALL_STATUSES = 'all';
const STATUSES = ['sent', 'delivered', 'undeliverable', 'failed', 'skipped'];

const fetchUsers = async (input, page, resolve) => {
  const resp = await Request.get('/admin/users', { sorter: 'name.asc', qs: input, page });
  resolve({
    total_count: resp.meta.total,
    items: resp.data.map((user) => ({
      id: Number(user.id),
      name: user.cpf ? `${user.name} · ${maskCpf(user.cpf)}` : user.name,
    })),
  });
};

// Mesmo formato que o filtro genérico das listas manda para a API.
const buildFilters = (sentFrom, sentUntil, status) => {
  const filters = [];
  const from = sentFrom ? moment(sentFrom).startOf('day').subtract(1, 'second').format() : null;
  const until = sentUntil ? moment(sentUntil).endOf('day').format() : null;
  if (from && until) filters.push({ column: 'sent_at', operation: 'between', value: `${from}|${until}` });
  else if (from) filters.push({ column: 'sent_at', operation: 'greaterThan', value: from });
  else if (until) filters.push({ column: 'sent_at', operation: 'lessThan', value: until });
  if (status !== ALL_STATUSES) filters.push({ column: 'status', operation: 'equal', value: status });
  return filters.length ? JSON.stringify({ groups: [{ condition: 'and', filters }] }) : '';
};

const PanelContext = createContext({});

// O card da lista é montado pelo container da tabela; definir o painel fora do componente da página
// evita remontar a tabela (e perder o foco dos campos) a cada tecla digitada no filtro.
function InvitationsPanel({ children }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key) => t(`whatsappCampaign.extras.${key}`);
  const {
    sentFrom, setSentFrom, sentUntil, setSentUntil, status, setStatus, downloadPdf, downloading,
  } = useContext(PanelContext);

  const statusOptions = [
    { id: ALL_STATUSES, name: tr('statusAll') },
    ...STATUSES.map((value) => ({ id: value, name: tr(`statuses.${value}`) })),
  ];

  return (
    <Paper
      Icon={WhatsAppIcon}
      iconColor="green"
      title={tr('invitations')}
      actions={(
        <div className={classes.paperActions}>
          <Button
            variant="contained"
            color="primary"
            className={classes.smallButton}
            startIcon={<SaveAltIcon />}
            disabled={downloading}
            onClick={downloadPdf}
          >
            {tr('downloadPdf')}
          </Button>
        </div>
      )}
    >
      <div className={classes.filterRow}>
        <GridContainer>
          <GridItem xs={12} md={3}>
            <InputControl
              name="sent_from"
              value={sentFrom}
              label={tr('sentFrom')}
              inputComponent={InputDate}
              InputProps={{ type: 'date' }}
              onChange={(date) => setSentFrom(date || '')}
            />
          </GridItem>
          <GridItem xs={12} md={3}>
            <InputControl
              name="sent_until"
              value={sentUntil}
              label={tr('sentUntil')}
              inputComponent={InputDate}
              InputProps={{ type: 'date' }}
              onChange={(date) => setSentUntil(date || '')}
            />
          </GridItem>
          <GridItem xs={12} md={4}>
            <InputControl
              name="status"
              value={status}
              label={tr('statusFilter')}
              inputComponent={InputSelect}
              onChange={({ status: selected }) => setStatus(selected || ALL_STATUSES)}
              InputProps={{ options: statusOptions, outValueType: 'string' }}
            />
          </GridItem>
        </GridContainer>
      </div>
      {children}
    </Paper>
  );
}

function Invitations() {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`whatsappCampaign.extras.${key}`, options);
  const trAttr = (attr) => t(`whatsappCampaign.invitations.attributes.${attr}`);
  const listEl = useRef(null);
  const [user, setUser] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [sentFrom, setSentFrom] = useState('');
  const [sentUntil, setSentUntil] = useState('');
  const [status, setStatus] = useState(ALL_STATUSES);
  const [downloading, setDownloading] = useState(false);

  const filters = useMemo(() => buildFilters(sentFrom, sentUntil, status), [sentFrom, sentUntil, status]);
  const hasFilters = Boolean(filters);
  const formatDate = (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '');

  async function invite() {
    if (!user || !user.id) {
      toast.warn(tr('inviteUserRequired'));
      return;
    }
    setInviting(true);
    try {
      await Request.post(`${PATH}/invitations`, { data: { attributes: { user_id: user.id } } }, {}, { controller: 'whatsappCampaign.invitations', disableToastErrors: true });
      toast.success(tr('inviteSent'));
      setUser(null);
    } catch (e) {
      const reason = e && e.list && e.list[0] && e.list[0].detail;
      toast.error(reason ? tr(`reasons.${reason}`, { defaultValue: reason }) : t('form-toast-error-title'));
    } finally {
      setListVersion((version) => version + 1);
      setInviting(false);
    }
  }

  async function downloadPdf() {
    setDownloading(true);
    const toastId = toast.info(tr('pdfGenerating'), { autoClose: false });
    try {
      const params = new URLSearchParams({ sorter: 'sent_at.desc', ...(hasFilters ? { filters } : {}) });
      const res = await fetch(`${URL}${PATH}/invitations/pdf?${params}`, { headers: await Request.getHeaders() });
      if (res.status === 429) {
        const { error } = await res.json();
        toast.warn(error);
        return;
      }
      if (!res.ok) throw new Error(res.statusText);

      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `convites-whatsapp-${moment().format('YYYY-MM-DD-HHmm')}.pdf`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
      toast.success(tr('pdfDone'));
    } catch (e) {
      toast.error(tr('pdfError'));
    } finally {
      toast.dismiss(toastId);
      setDownloading(false);
    }
  }

  const panel = { sentFrom, setSentFrom, sentUntil, setSentUntil, status, setStatus, downloadPdf, downloading };

  return (
    <PanelContext.Provider value={panel}>
      <Paper Icon={WhatsAppIcon} iconColor="green" title={tr('invite')}>
        <div className={classes.inviteRow}>
          <div className={classes.inviteField}>
            <InputControl
              name="user"
              value={user}
              label={tr('inviteUser')}
              inputComponent={InputSelectAsync}
              onChange={({ user: selected }) => setUser(selected)}
              InputProps={{
                isClearable: true,
                handleFetch: fetchUsers,
                createEnabled: false,
                error: false,
              }}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            className={`${classes.smallButton} ${classes.inviteButton}`}
            disabled={inviting}
            onClick={invite}
          >
            {tr('invite')}
          </Button>
        </div>
      </Paper>

      <List
        key={listVersion}
        listEl={listEl}
        path={`${PATH}/invitations`}
        actions={[]}
        options={{ toolbar: false }}
        fixedQuery={hasFilters ? { filters } : {}}
        fetchFlag={filters}
        paperProps={{ Icon: WhatsAppIcon, iconColor: 'green', title: tr('invitations') }}
        components={{ Container: InvitationsPanel }}
        columns={[
          { title: trAttr('id'), field: 'id', type: 'numeric', precision: 0, includeThousandsSeparator: false },
          { title: trAttr('user_id'), field: 'user_id', type: 'numeric', precision: 0, includeThousandsSeparator: false },
          { title: trAttr('user_name'), field: 'user_name', type: 'string' },
          { title: trAttr('phone'), field: 'phone', type: 'string' },
          { title: trAttr('status'), field: 'status', type: 'string' },
          { title: trAttr('skip_reason'), field: 'skip_reason', type: 'string' },
          { title: trAttr('sent_at'), field: 'sent_at', type: 'string' },
          { title: trAttr('delivered_at'), field: 'delivered_at', type: 'string' },
          { title: trAttr('error'), field: 'error', type: 'string' },
          { title: trAttr('evaluated_at'), field: 'evaluated_at', type: 'string' },
        ]}
        context={WhatsappInvitationContext}
        recordMap={(record) => ({
          ...record,
          status: tr(`statuses.${record.status}`, { defaultValue: record.status }),
          skip_reason: record.skip_reason ? tr(`reasons.${record.skip_reason}`, { defaultValue: record.skip_reason }) : '',
          sent_at: formatDate(record.sent_at),
          delivered_at: formatDate(record.delivered_at),
          evaluated_at: formatDate(record.evaluated_at),
        })}
      />
    </PanelContext.Provider>
  );
}

export default React.memo(Invitations);
